import { ref, computed } from 'vue';
import type { Ref } from 'vue';

export interface PushNotificationPayload {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  image?: string;
  data?: Record<string, unknown>;
  actions?: Array<{
    action: string;
    title: string;
    icon?: string;
  }>;
  requireInteraction?: boolean;
  silent?: boolean;
  tag?: string;
  renotify?: boolean;
  timestamp?: number;
  vibrate?: number[];
  url?: string;
}

export interface NotificationPermissionState {
  granted: boolean;
  denied: boolean;
  default: boolean;
  loading: boolean;
}

export const usePushNotifications = () => {
  const permission = ref<NotificationPermission>('default');
  const isSupported = ref(false);
  const subscription: Ref<PushSubscription | null> = ref(null);
  const isSubscribed = computed(() => !!subscription.value);

  const checkSupport = () => {
    isSupported.value = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  };

  const getPermissionStatus = () => {
    if (!isSupported.value) return 'default';
    return Notification.permission;
  };

  const requestPermission = async (): Promise<NotificationPermission> => {
    if (!isSupported.value) {
      throw new Error('Push notifications are not supported in this browser');
    }

    try {
      const result = await Notification.requestPermission();
      permission.value = result;
      return result;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      throw error;
    }
  };

  const registerServiceWorker = async () => {
    if (!('serviceWorker' in navigator)) {
      throw new Error('Service workers are not supported');
    }

    try {
      let registration = await navigator.serviceWorker.getRegistration();

      if (!registration) {
        registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/'
        });
      }

      await navigator.serviceWorker.ready;

      if (registration.installing) {
        await new Promise<void>((resolve) => {
          const installingWorker = registration.installing;
          if (installingWorker) {
            installingWorker.addEventListener('statechange', () => {
              if (installingWorker.state === 'activated') {
                resolve();
              }
            });
          } else {
            resolve();
          }
        });
      }

      await new Promise((resolve) => setTimeout(resolve, 100));

      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      throw error;
    }
  };

  const cleanupOldSubscriptions = async (): Promise<void> => {
    try {
      subscription.value = null;

      const registrations = await navigator.serviceWorker.getRegistrations();

      for (const registration of registrations) {
        try {
          const existingSub = await registration.pushManager.getSubscription();
          if (existingSub) {
            await existingSub.unsubscribe();
            await new Promise((resolve) => setTimeout(resolve, 100));
          }
        } catch (err) {
          console.warn('Error removing old subscription:', err);
        }
      }

      await new Promise((resolve) => setTimeout(resolve, 500));
    } catch (error) {
      console.warn('Error during cleanup:', error);
    }
  };

  const deepCleanup = async (): Promise<{
    success: boolean;
    subsRemoved: number;
    swsRemoved: number;
    needsReload: boolean;
  }> => {
    try {
      subscription.value = null;

      const registrations = await navigator.serviceWorker.getRegistrations();

      let subsRemoved = 0;
      for (const registration of registrations) {
        try {
          const existingSub = await registration.pushManager.getSubscription();
          if (existingSub) {
            await existingSub.unsubscribe();
            subsRemoved++;
            await new Promise((resolve) => setTimeout(resolve, 200));
          }
        } catch (err) {
          console.warn('Error removing subscription:', err);
        }
      }

      let swsRemoved = 0;
      for (const registration of registrations) {
        try {
          const unregResult = await registration.unregister();
          if (unregResult) {
            swsRemoved++;
          }
          await new Promise((resolve) => setTimeout(resolve, 200));
        } catch (err) {
          console.warn('Error unregistering:', err);
        }
      }

      await new Promise((resolve) => setTimeout(resolve, 3000));

      const remainingRegs = await navigator.serviceWorker.getRegistrations();

      if (remainingRegs.length > 0) {
        console.warn(`${remainingRegs.length} service worker(s) still exist`);
      }

      return {
        success: true,
        subsRemoved,
        swsRemoved,
        needsReload: true
      };
    } catch (error) {
      console.error('Deep cleanup error:', error);
      return {
        success: false,
        subsRemoved: 0,
        swsRemoved: 0,
        needsReload: false
      };
    }
  };

  const subscribe = async (vapidPublicKey?: string, autoRetryWithDeepCleanup = true): Promise<PushSubscription> => {
    try {
      await cleanupOldSubscriptions();

      if (!vapidPublicKey) {
        throw new Error('VAPID public key is required for push notifications');
      }

      await registerServiceWorker();

      if (permission.value !== 'granted') {
        await requestPermission();
      }

      if (permission.value !== 'granted') {
        throw new Error('Notification permission not granted');
      }

      const registration = await navigator.serviceWorker.ready;

      if (registration.installing) {
        await new Promise<void>((resolve) => {
          const installingWorker = registration.installing;
          if (installingWorker) {
            installingWorker.addEventListener('statechange', () => {
              if (installingWorker.state === 'activated') {
                resolve();
              }
            });
          } else {
            resolve();
          }
        });
      }

      await new Promise((resolve) => setTimeout(resolve, 300));

      if (!registration.active) {
        throw new Error('Service worker is not active. Please refresh the page and try again.');
      }

      const pushManager = registration.pushManager;

      if (!pushManager) {
        throw new Error('PushManager is not available. Push notifications may not be supported in this context.');
      }

      const doubleCheck = await pushManager.getSubscription();
      if (doubleCheck) {
        await doubleCheck.unsubscribe();
        await new Promise((resolve) => setTimeout(resolve, 1000));

        const tripleCheck = await pushManager.getSubscription();
        if (tripleCheck) {
          throw new Error('Could not remove old subscription. Run fixPushNotifications() and reload.');
        }
      }

      const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);

      if (applicationServerKey.length !== 65) {
        throw new Error(`Invalid application server key length: ${applicationServerKey.length}, expected 65 bytes`);
      }

      let newSubscription: PushSubscription;

      try {
        const freshKeyArray = new Uint8Array(applicationServerKey.length);
        freshKeyArray.set(applicationServerKey);

        const subscribePromise = pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: freshKeyArray
        });

        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('Subscription timeout after 60 seconds')), 60000);
        });

        newSubscription = await Promise.race([subscribePromise, timeoutPromise]);
      } catch (subscribeError: unknown) {
        const error = subscribeError as Error;

        if (error.message.includes('timeout')) {
          throw new Error(
            `Push subscription timeout. Device may be corrupted. Run fixPushNotifications() in console and reload.`
          );
        }

        if (error.name === 'AbortError' && error.message.includes('push service error')) {
          throw new Error(
            `DEVICE CORRUPTED - Push service error\n\n` +
              `Run in console (F12): await fixPushNotifications()\n\n` +
              `Or manually: DevTools → Application → Storage → Clear site data, then reload.\n\n` +
              `Technical error: ${error.message}`
          );
        }

        throw new Error(`Push subscription failed: ${error.message || String(subscribeError)}`);
      }

      subscription.value = newSubscription;

      const verifySubscription = await pushManager.getSubscription();
      if (verifySubscription) {
        subscription.value = verifySubscription;
      }

      return newSubscription;
    } catch (error) {
      console.error('Subscription error:', error);
      subscription.value = null;

      const err = error as Error;
      if (autoRetryWithDeepCleanup && err.message.includes('DEVICE CORRUPTED')) {
        try {
          const result = await deepCleanup();
          if (result && result.needsReload) {
            setTimeout(() => {
              window.location.reload();
            }, 2000);
          }
        } catch (cleanupError) {
          console.error('Auto cleanup failed:', cleanupError);
        }
      }

      throw error;
    }
  };

  const unsubscribe = async (): Promise<boolean> => {
    if (!subscription.value) {
      return true;
    }

    try {
      try {
        await $fetch('/api/push/unsubscribe', {
          method: 'POST',
          body: {
            endpoint: subscription.value.endpoint
          }
        });
      } catch (serverError) {
        console.error('Error removing subscription from server:', serverError);
      }

      const result = await subscription.value.unsubscribe();
      if (result) {
        subscription.value = null;
      }
      return result;
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
      throw error;
    }
  };

  const getSubscription = async (): Promise<PushSubscription | null> => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const existingSubscription = await registration.pushManager.getSubscription();

      subscription.value = existingSubscription;

      if (existingSubscription) {
        try {
          const checkResponse = await $fetch<{
            success: boolean;
            isSubscribed: boolean;
          }>(`/api/push/subscription-check?endpoint=${encodeURIComponent(existingSubscription.endpoint)}`);

          if (!checkResponse.isSubscribed) {
            console.warn('Subscription not found in database, re-syncing...');
            subscription.value = null;
            return null;
          }
        } catch (error) {
          console.error('Error checking subscription in database:', error);
        }
      }

      return existingSubscription;
    } catch (error) {
      console.error('Error getting subscription:', error);
      subscription.value = null;
      return null;
    }
  };

  const sendTestNotification = (payload: PushNotificationPayload) => {
    if (permission.value !== 'granted') {
      throw new Error('Notification permission not granted');
    }

    const notificationOptions: NotificationOptions & {
      actions?: Array<{
        action: string;
        title: string;
        icon?: string;
      }>;
      renotify?: boolean;
      timestamp?: number;
      vibrate?: number[];
    } = {
      body: payload.body,
      icon: payload.icon || '/icons/icon-192x192.png',
      badge: payload.badge || '/icons/icon-192x192.png',
      data: payload.data || {},
      actions: payload.actions || [],
      requireInteraction: payload.requireInteraction || false,
      silent: payload.silent || false,
      tag: payload.tag,
      renotify: payload.renotify || false,
      timestamp: payload.timestamp || Date.now(),
      vibrate: payload.vibrate || [200, 100, 200]
    };

    const notification = new Notification(payload.title, notificationOptions);

    if (!payload.requireInteraction) {
      setTimeout(() => {
        notification.close();
      }, 5000);
    }

    return notification;
  };

  const sendServerTestNotification = async (
    payload: PushNotificationPayload
  ): Promise<{ success: boolean; message: string }> => {
    if (!subscription.value) {
      throw new Error('No active subscription. Please subscribe first using subscribe()');
    }

    try {
      const response = await $fetch('/api/push/broadcast', {
        method: 'POST',
        body: {
          subscriptions: [subscription.value],
          payload: {
            title: payload.title,
            body: payload.body,
            icon: payload.icon,
            badge: payload.badge,
            image: payload.image,
            data: payload.data,
            actions: payload.actions,
            requireInteraction: payload.requireInteraction,
            silent: payload.silent,
            tag: payload.tag,
            renotify: payload.renotify,
            timestamp: payload.timestamp,
            vibrate: payload.vibrate,
            url: payload.url
          }
        }
      });

      return response as { success: boolean; message: string };
    } catch (error) {
      console.error('Error sending server test notification:', error);
      throw error;
    }
  };

  const initialize = async () => {
    checkSupport();

    if (!isSupported.value) {
      console.warn('Push notifications are not supported in this browser');
      return;
    }

    permission.value = getPermissionStatus();

    await getSubscription();
  };

  const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
    try {
      const base64 = base64String.replace(/-/g, '+').replace(/_/g, '/');
      const paddingNeeded = (4 - (base64.length % 4)) % 4;
      const paddedBase64 = base64 + '='.repeat(paddingNeeded);

      const rawData = window.atob(paddedBase64);

      const outputArray = new Uint8Array(rawData.length);
      for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
      }

      if (outputArray.length !== 65) {
        console.warn(`VAPID key length is ${outputArray.length} bytes, expected 65. This may cause issues.`);
      }

      return outputArray;
    } catch (error: unknown) {
      console.error('Error converting VAPID key:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      throw new Error(`VAPID key conversion failed: ${errorMessage}`);
    }
  };

  if (typeof window !== 'undefined') {
    const w = window as Window & {
      __pushNotificationsDeepCleanup?: typeof deepCleanup;
      fixPushNotifications?: typeof deepCleanup;
    };

    w.__pushNotificationsDeepCleanup = deepCleanup;
    w.fixPushNotifications = deepCleanup;
  }

  return {
    permission: computed(() => permission.value),
    isSupported: computed(() => isSupported.value),
    subscription: computed(() => subscription.value),
    isSubscribed,
    initialize,
    requestPermission,
    subscribe,
    unsubscribe,
    getSubscription,
    sendTestNotification,
    sendServerTestNotification,
    cleanupOldSubscriptions,
    deepCleanup,
    permissionState: computed(
      (): NotificationPermissionState => ({
        granted: permission.value === 'granted',
        denied: permission.value === 'denied',
        default: permission.value === 'default',
        loading: false
      })
    )
  };
};
